import React, { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";
import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import StarIcon from "@mui/icons-material/Star";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import PostAddIcon from "@mui/icons-material/PostAdd";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import DraftsOutlinedIcon from "@mui/icons-material/DraftsOutlined";
import { Box, Divider, LinearProgress, Skeleton, Stack, Typography } from "@mui/material";
import { Bar } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import { capitalizeFirstLetter } from "src/Utils/Function";
import { useTailwindBrand } from "../../shared";
import ButtonField from "../../Ui/ButtonField";
import Functions from "../../../Helpers/Functions";
import { DataChartType } from "../../../Hooks/Dashboard/useHome";
import {
  ENV_VERIFIED_TRAINER,
  getActiveClientBrandConfig,
  getActivePrestationBrandConfig,
  isBrandActive,
} from "../../../config/environment.config";
import PunctualCourseRepository from "../../../Repositories/PunctualCourseRepository";
import { vtRemunerationLabel } from "../../../Utils/VerifiedTrainerAmounts";
import { SponsorshipMetric } from "./SponsorshipCard";
import MlmNetworkWidget from "./MlmNetworkWidget";
import { SponsorshipMetrics } from "./Resume";
import UpcomingCalendar from "./UpcomingCalendar";
import NotificationsWidget from "./NotificationsWidget";
import HelpCard from "./HelpCard";

ChartJS.register(
  CategoryScale,
  ChartDataLabels,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

/* ── Types ─────────────────────────────────────────────────────────── */

export interface AllBordTabProps {
  teachr?: { id?: number; isAE?: boolean; isValidated?: boolean; firstName?: string };
  totalStudents: number;
  totalStudentsNoActive: number;
  upcomingCourses: number;
  dataChart?: DataChartType;
  amountAndDurationData?: DataChartType;
  course?: any[];
  brandColors: Record<string, string>;
  primaryTextClass?: string;
  isLoading?: boolean;
  onDeclare: () => void;
  onAddStudent: () => void;
  /** Brouillons v2 : bouton « Mes brouillons » quand des brouillons attendent. */
  onDrafts?: () => void;
  draftsCount?: number;
  sponsorshipMetrics?: SponsorshipMetrics;
  sponsorshipCards?: SponsorshipMetric[];
  onNavigateToSponsorship?: () => void;
}

const PERIODS = [
  { label: "3 derniers mois", value: "3month" },
  { label: "6 derniers mois", value: "6month" },
  { label: "12 derniers mois", value: "12month" },
  // { label: "Depuis le début", value: "startTeach" },
] as const;

type PeriodKey = (typeof PERIODS)[number]["value"];

/* ── Helpers ─────────────────────────────────────────────────────── */

const formatKey = (date: moment.MomentInput) => {
  const f = moment(date).format("MMM YYYY");
  return (f.charAt(0).toUpperCase() + f.slice(1)).toUpperCase();
};

const fmt = (n: number) =>
  n.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const IS_VT = isBrandActive(ENV_VERIFIED_TRAINER);
/** Rémunération enregistrée du mois (teachr_price × heures), hors TVA : ne dépend pas du taux de TVA courant. */
const remun = (c: any) => parseFloat(c?.remuneration ?? c?.teachr_price ?? "0");
/** Montant net enregistré du mois (teachr_price × heures) : la clé de conversion vers le prix total, pas la rémunération. */
const net = (c: any) => parseFloat(c?.teachr_price ?? "0");
/** Commissions enregistrées du mois (fees × heures). */
const fees = (c: any) => parseFloat(c?.fees ?? "0");
/** Montant mis en avant : prix total TTC chez Verified Trainer (net + commissions), rémunération ailleurs. */
const rev = (c: any) => (IS_VT ? net(c) + fees(c) : remun(c));

/* ── AllBordChart sub-component ──────────────────────────────────── */

const AllBordChart: React.FC = () => {
  const teachr = useSelector((state: any) => state.user?.currentUser?.teachr);
  const { brandConfig } = useTailwindBrand();
  const primary = brandConfig.colors.primary;
  const secondary = brandConfig.colors.secondary;
  const ff = brandConfig.fontFamily;

  const [period, setPeriod] = useState<PeriodKey>("6month");
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  const chartRef = useRef<any>(null);
  const [chartJsData, setChartJsData] = useState<any>({ labels: [], datasets: [] });

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  const { data: periodData, isLoading: isChartLoading } = useQuery({
    queryKey: ["allBordChart", teachr?.id, period],
    queryFn: () =>
      PunctualCourseRepository.retreivePaidAmountInChart(teachr?.id!, period),
    enabled: !!teachr?.id,
  });

  // Rémunération de la période telle que facturée (part HT + TVA enregistrées), à défaut le net
  const totalPeriod = periodData?.remuneration ?? periodData?.totalAmount;
  // Verified Trainer : prix total TTC de la période = net enregistré + commissions enregistrées
  const totalPeriodTtc = IS_VT && periodData?.totalAmount != null
    ? parseFloat(periodData.totalAmount) + parseFloat(periodData?.fees ?? "0")
    : null;

  useEffect(() => {
    const chart = chartRef.current;
    const courses = periodData?.courses ?? [];
    const labels = courses.map((c: any) => c.startmonth);
    const values = courses.map((c: any) => rev(c));
    // Verified Trainer : seconde série pour distinguer la rémunération du prix total
    const remunerations = courses.map((c: any) => remun(c));

    let bg: any = `${primary}60`;
    let borderColor: any = primary;

    if (chart?.ctx && chart?.height) {
      const h = chart.height;
      const g = chart.ctx.createLinearGradient(0, 0, 0, h);
      g.addColorStop(0, secondary);
      g.addColorStop(1, primary);
      const gb = chart.ctx.createLinearGradient(0, 0, 0, h);
      gb.addColorStop(0, secondary);
      gb.addColorStop(1, primary);
      bg = g;
      borderColor = gb;
    }

    setChartJsData({
      labels,
      datasets: [
        {
          label: IS_VT ? "Prix total" : "Revenus",
          data: values,
          backgroundColor: bg,
          borderColor,
          borderWidth: 1,
          borderRadius: 12,
          borderSkipped: false,
        },
        ...(IS_VT
          ? [{
            label: vtRemunerationLabel(teachr),
            data: remunerations,
            backgroundColor: `${secondary}99`,
            borderColor: secondary,
            borderWidth: 1,
            borderRadius: 12,
            borderSkipped: false,
            datalabels: { color: secondary, borderColor: secondary },
          }]
          : []),
      ],
    });
  }, [periodData, primary, secondary, teachr]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    animation: { duration: 900, easing: "easeInOutQuart" as const },
    interaction: { intersect: false, mode: "index" as const },
    plugins: {
      legend: { display: IS_VT, position: "bottom" as const, labels: { font: { size: 10, family: ff }, boxWidth: 10 } },
      tooltip: {
        cornerRadius: 10,
        titleFont: { size: 11, weight: "bold" as const, family: ff },
        bodyFont: { size: 11, family: ff },
        callbacks: {
          title: (items: any[]) => `📅 ${items[0].label}`,
          afterTitle: (items: any[]) =>
            IS_VT ? "" : `💰 ${parseFloat(items[0].raw).toLocaleString("fr-FR", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })} €`,
          // Verified Trainer : une ligne par série (prix total, rémunération)
          label: (item: any) =>
            IS_VT ? `${item.dataset.label} : ${fmt(parseFloat(item.raw))} €` : "",
        },
      },
      datalabels: {
        display: !isMobile,
        color: primary,
        anchor: "end" as const,
        align: "top" as const,
        font: { size: 10, weight: "600" as const, family: ff },
        formatter: (value: any) => {
          const n = parseFloat(value);
          if (n >= 1000) return `${(n / 1000).toFixed(1)}k€`;
          return `${Math.round(n)}€`;
        },
        backgroundColor: "rgba(255,255,255,0.9)",
        borderColor: primary,
        borderWidth: 1,
        borderRadius: 6,
        padding: { top: 3, right: 4, bottom: 2, left: 4 },
      },
    },
    layout: { padding: { top: 30, bottom: 0, left: 0, right: 0 } },
    scales: {
      x: {
        grid: { display: false },
        ticks: { font: { size: 10, family: ff }, color: primary },
      },
      y: {
        type: "linear" as const,
        beginAtZero: true,
        display: false,
        grid: { display: false },
      },
    },
  };

  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        height: "100%",
        borderRadius: 3,
        bgcolor: "background.paper",
        border: "1px solid",
        borderColor: "divider",
        boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      {/* Header: title + total + period pills */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        gap={1.5}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <AutoGraphIcon sx={{ color: primary, fontSize: 18 }} />
          <Box>
            <Typography variant="h6" fontWeight={800} sx={{ fontFamily: ff, color: primary, lineHeight: 1.1 }}>
              Tendance des revenus
            </Typography>
            {totalPeriod != null && (
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", fontFamily: ff }}
              >
                {totalPeriodTtc != null ? "Prix total : " : "Total : "}
                <strong style={{ color: primary }}>
                  {fmt(totalPeriodTtc ?? parseFloat(totalPeriod))} €
                </strong>
                {totalPeriodTtc != null && (
                  <> · {vtRemunerationLabel(teachr)} : <strong style={{ color: secondary }}>{fmt(parseFloat(totalPeriod))} €</strong></>
                )}
              </Typography>
            )}
          </Box>
        </Stack>

        {/* Period selector pills */}
        <Stack direction="row" spacing={0.5} flexWrap="wrap">
          {PERIODS.map((p) => (
            <Box
              key={p.value}
              onClick={() => setPeriod(p.value)}
              sx={{
                px: 1.3,
                py: 0.35,
                borderRadius: 10,
                cursor: "pointer",
                fontSize: "0.68rem",
                fontWeight: period === p.value ? 700 : 500,
                fontFamily: ff,
                bgcolor: period === p.value ? primary : "grey.100",
                color: period === p.value ? "white" : "text.secondary",
                border: "1px solid",
                borderColor: period === p.value ? primary : "transparent",
                transition: "all 0.18s",
                "&:hover": {
                  bgcolor:
                    period === p.value ? primary : `${primary}18`,
                  color: period === p.value ? "white" : primary,
                },
                userSelect: "none",
                whiteSpace: "nowrap",
              }}
            >
              {p.label}
            </Box>
          ))}
        </Stack>
      </Stack>

      {/* Chart area */}
      {isChartLoading && chartJsData.labels.length === 0 ? (
        <Skeleton
          variant="rectangular"
          height={220}
          sx={{ borderRadius: 2 }}
          animation="wave"
        />
      ) : chartJsData.labels.length === 0 ? (
        <Box
          sx={{
            flex: 1,
            minHeight: 220,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "grey.50",
            borderRadius: 2,
            border: "1px dashed",
            borderColor: "divider",
          }}
        >
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", fontFamily: ff }}
          >
            Vos données apparaîtront ici après vos premières déclarations.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ flex: 1, minHeight: 220, position: "relative" }}>
          {isChartLoading && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                bgcolor: "rgba(255,255,255,0.55)",
                zIndex: 1,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography
                variant="caption"
                sx={{ color: primary, fontFamily: ff, fontWeight: 600 }}
              >
                Mise à jour…
              </Typography>
            </Box>
          )}
          <Bar ref={chartRef} data={chartJsData} options={options as any} />
        </Box>
      )}
    </Box>
  );
};

/* ── AllBordTab ──────────────────────────────────────────────────── */

const AllBordTab: React.FC<AllBordTabProps> = ({
  teachr,
  totalStudents,
  totalStudentsNoActive,
  upcomingCourses,
  dataChart,
  course = [],
  isLoading = false,
  onDeclare,
  onAddStudent,
  onDrafts,
  draftsCount = 0,
  sponsorshipMetrics,
  sponsorshipCards = [],
  onNavigateToSponsorship,
}) => {
  const { brandConfig } = useTailwindBrand();
  const primary = brandConfig.colors.primary;
  const secondary = brandConfig.colors.secondary;
  const ff = brandConfig.fontFamily;
  // Taux de TVA du coach, pour préciser « TTC » sur la rémunération quand il y est assujetti
  const teachrRates = useSelector((state: any) => state.user?.currentUser?.teachr);

  /* ── Monthly computations ── */
  const currentMonthKey = formatKey(moment());
  const lastMonthKey = formatKey(moment().subtract(1, "month"));

  const currMonth = dataChart?.courses?.find((c) => {
    const key = moment(c.startmonth, "MMM YYYY", "fr").format("MMM YYYY");
    return (key.charAt(0).toUpperCase() + key.slice(1)).toUpperCase() === currentMonthKey;
  });

  const prevMonth = dataChart?.courses?.find((c) => {
    const key = moment(c.startmonth, "MMM YYYY", "fr").format("MMM YYYY");
    return (key.charAt(0).toUpperCase() + key.slice(1)).toUpperCase() === lastMonthKey;
  });

  const currentRevenue = rev(currMonth);
  const lastRevenue = rev(prevMonth);
  // Verified Trainer : rémunération du mois, affichée à côté du prix total
  const currentRemuneration = remun(currMonth);
  const currentHours = parseFloat(currMonth?.duration ?? "0");
  const trend =
    lastRevenue > 0
      ? ((currentRevenue - lastRevenue) / lastRevenue) * 100
      : null;
  const trendUp = trend !== null && trend > 0;
  const trendDown = trend !== null && trend < 0;

  /* ── Historical totals (from dataChart totals fields) ── */
  const { totalRevenue, totalDuration, totalShown } = useMemo(
    () => ({
      // rémunération telle que facturée depuis le début, à défaut le net
      totalRevenue: dataChart?.totalRemuneration ?? dataChart?.total,
      totalDuration: dataChart?.totalDuration,
      // Verified Trainer : prix total TTC depuis le début = net enregistré + commissions enregistrées
      totalShown: IS_VT ? (dataChart?.total ?? 0) + (dataChart?.totalFees ?? 0) : (dataChart?.totalRemuneration ?? dataChart?.total),
    }),
    [dataChart?.total, dataChart?.totalDuration, dataChart?.totalFees, dataChart?.totalRemuneration]
  );

  /* ── Client health ── */
  const totalClients = totalStudents + totalStudentsNoActive;
  const activePercent = totalClients > 0 ? (totalStudents / totalClients) * 100 : 0;
  const canDeclare = !!(teachr?.isAE && teachr?.isValidated);
  const showSponsorship = canDeclare && !!sponsorshipMetrics;

  /* ── Loading skeleton ── */
  if (isLoading) {
    return (
      <div className="space-y-4 animate-pulse">
        <div className="h-36 bg-gray-200 rounded-2xl" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-24 bg-gray-200 rounded-xl" />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 h-72 bg-gray-200 rounded-xl" />
          <div className="h-72 bg-gray-200 rounded-xl" />
        </div>
      </div>
    );
  }

  const quickActions = (
    <Box sx={{ p: { xs: 2, md: 2.5 } }}>
      <Typography variant="subtitle2" fontWeight={700} sx={{ fontFamily: ff, color: primary, mb: 1.5 }}>
        ⚡ Actions rapides
      </Typography>
      <Stack spacing={1.2}>
        {canDeclare && (
          <ButtonField label={`Déclarer un ${getActivePrestationBrandConfig("")}`} variant="contained" onClick={onDeclare} endIcon={<PostAddIcon />} className="w-full" />
        )}
        <ButtonField label="Ajouter un élève" variant="outlined" onClick={onAddStudent} endIcon={<PersonAddAlt1Icon />} className="w-full" />
        {onDrafts && draftsCount > 0 && (
          <ButtonField label={`Mes brouillons (${draftsCount})`} variant="outlined" onClick={onDrafts} endIcon={<DraftsOutlinedIcon />} className="w-full" />
        )}
      </Stack>
    </Box>
  );

  return (
    <div className="space-y-4 animate-fadeIn">
      {/* Actions rapides en tête sur mobile pour rendre les CTA immédiatement visibles */}
      <Box sx={{ display: { xs: "block", lg: "none" }, borderRadius: 3, bgcolor: "background.paper", border: "1px solid", borderColor: "divider", boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
        {quickActions}
      </Box>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
        <div className="lg:col-span-2 flex flex-col gap-4">
          <Box sx={{ borderRadius: 3, overflow: "hidden", boxShadow: "0 4px 20px rgba(0,0,0,0.07)", bgcolor: "background.paper", border: "1px solid", borderColor: "divider" }}>
            <Box sx={{ height: 5, background: `linear-gradient(90deg, ${primary}, ${secondary})` }} />
            <Box sx={{ px: { xs: 2.5, md: 3 }, pt: 2.5, pb: 2 }}>
              <Stack direction="row" alignItems="flex-start" justifyContent="space-between" mb={2.5} flexWrap="wrap" gap={1}>
                <Stack direction="row" alignItems="center" spacing={1.2}>
                  <AutoGraphIcon sx={{ color: primary, fontSize: 22 }} />
                  <Box>
                    <Typography variant="h6" fontWeight={800} sx={{ fontFamily: ff, color: primary, lineHeight: 1.1 }}>
                      Synthèse d'activité
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary", fontFamily: ff }}>
                      {capitalizeFirstLetter(moment().format("MMMM YYYY"))}
                    </Typography>
                  </Box>
                </Stack>
                {trend !== null && (
                  <Stack direction="row" alignItems="center" spacing={0.5} sx={{ px: 1.5, py: 0.5, borderRadius: 10, bgcolor: trendUp ? "#dcfce7" : trendDown ? "#fee2e2" : "grey.100", color: trendUp ? "#15803d" : trendDown ? "#b91c1c" : "text.secondary" }}>
                    {trendUp ? <TrendingUpIcon sx={{ fontSize: 15 }} /> : trendDown ? <TrendingDownIcon sx={{ fontSize: 15 }} /> : <TrendingFlatIcon sx={{ fontSize: 15 }} />}
                    <Typography variant="caption" fontWeight={700} sx={{ fontSize: "0.72rem", fontFamily: ff }}>
                      {trendUp ? "+" : ""}{trend.toFixed(1)}% vs {moment().subtract(1, "month").format("MMMM")}
                    </Typography>
                  </Stack>
                )}
              </Stack>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 2.5, sm: 4 }} divider={<Divider orientation="vertical" flexItem sx={{ display: { xs: "none", sm: "block" } }} />}>
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 600, textTransform: "uppercase", fontSize: "0.6rem", letterSpacing: "0.7px", color: "text.secondary", fontFamily: ff }}>
                    {IS_VT ? "💶 Prix total déclaré ce mois" : "💶 Revenus déclarés ce mois"}
                  </Typography>
                  <Typography variant="h4" fontWeight={800} sx={{ color: primary, fontFamily: ff, lineHeight: 1.1, mt: 0.25 }}>
                    {fmt(currentRevenue)} €
                  </Typography>
                  <Typography variant="caption" sx={{ color: "text.secondary", fontFamily: ff }}>
                    {IS_VT ? `${vtRemunerationLabel(teachrRates)} : ${fmt(currentRemuneration)} € · ` : ""}
                    {lastRevenue > 0 ? `vs ${fmt(lastRevenue)} € en ${moment().subtract(1, "month").format("MMMM")}` : "ce mois"}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 600, textTransform: "uppercase", fontSize: "0.6rem", letterSpacing: "0.7px", color: "text.secondary", fontFamily: ff }}>
                    ⏱ Heures réalisées
                  </Typography>
                  <Typography variant="h4" fontWeight={800} sx={{ color: primary, fontFamily: ff, lineHeight: 1.1, mt: 0.25 }}>
                    {currentHours > 0 ? Functions.renderDuration(currMonth?.duration) : "-"}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "text.secondary", fontFamily: ff }}>ce mois</Typography>
                </Box>
                {totalRevenue && totalRevenue > 0 && (
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 600, textTransform: "uppercase", fontSize: "0.6rem", letterSpacing: "0.7px", color: "text.secondary", fontFamily: ff }}>
                      📈 Total depuis le début
                    </Typography>
                    <Typography variant="h4" fontWeight={800} sx={{ color: primary, fontFamily: ff, lineHeight: 1.1, mt: 0.25 }}>
                      {fmt(totalShown ?? 0)} €
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary", fontFamily: ff }} className="font-bold">
                      {IS_VT ? `${vtRemunerationLabel(teachrRates)} : ${fmt(totalRevenue)} € · ` : ""}
                      {totalDuration && totalDuration > 0 ? `${Functions.renderDuration(String(totalDuration))} au total` : "cumulé"}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Box>

            {/* Strip activité */}
            <Box sx={{ px: { xs: 2.5, md: 3 }, py: 3, bgcolor: `${primary}07`, borderTop: "1px solid", borderColor: "divider" }}>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-3">
                {[
                  { icon: "👥", label: `${capitalizeFirstLetter(getActiveClientBrandConfig("s"))} actifs`, value: totalStudents, color: primary },
                  { icon: "⌛", label: `${capitalizeFirstLetter(getActiveClientBrandConfig("s"))} en attente`, value: totalStudentsNoActive, color: "#b45309" },
                  { icon: "📅", label: `${capitalizeFirstLetter(getActivePrestationBrandConfig("s"))} à venir`, value: upcomingCourses, color: primary },
                  { icon: "📆", label: "Mois actifs", value: (dataChart?.courses ?? []).length, color: primary },
                ].map((item, i) => (
                  <Stack key={i} direction="row" alignItems="center" spacing={1.2}>
                    <Typography sx={{ fontSize: "1.2rem", lineHeight: 1, flexShrink: 0 }}>{item.icon}</Typography>
                    <Box>
                      <Typography variant="subtitle2" fontWeight={700} sx={{ fontFamily: ff, color: item.color, lineHeight: 1 }}>{item.value}</Typography>
                      <Typography variant="caption" sx={{ color: "text.secondary", fontFamily: ff, lineHeight: 1.2 }}>{item.label}</Typography>
                    </Box>
                  </Stack>
                ))}
              </div>
            </Box>

            {/* Verified Trainer : filleuls par niveau et commissions validées (MLM) */}
            {IS_VT && canDeclare && teachr?.id && (
              <MlmNetworkWidget teachrId={teachr.id} onNavigate={onNavigateToSponsorship} />
            )}

            {/* Strip parrainage intégré */}
            {showSponsorship && (
              <Box sx={{ px: { xs: 2.5, md: 3 }, py: 4, borderTop: "1px solid", borderColor: "divider" }}>
                <Stack direction={{ xs: "column", sm: "column", md: "column", lg: "row" }} alignItems={{ xs: "flex-start", sm: "center" }} justifyContent="space-between" gap={2}>
                  {!sponsorshipMetrics!.hasSponsors && (
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box sx={{ p: 0.6, borderRadius: 1.5, bgcolor: `${primary}15`, flexShrink: 0 }}>
                        <StarIcon sx={{ color: primary, fontSize: 16 }} />
                      </Box>
                      <Typography variant="h6" fontWeight={800} sx={{ fontFamily: ff, color: primary, lineHeight: 1.1 }}>
                        {isBrandActive(ENV_VERIFIED_TRAINER) ? "🌟 Mon équipe" : "🤝 Parrainage"}
                      </Typography>
                      <Typography variant="caption" fontWeight={700} sx={{ fontFamily: ff, color: primary }}>
                        {isBrandActive(ENV_VERIFIED_TRAINER) ? "🌟 Mon équipe" : "🤝 Parrainage"}
                      </Typography>
                      <Typography variant="caption" sx={{ fontFamily: ff, color: "text.secondary" }}>
                        {isBrandActive(ENV_VERIFIED_TRAINER) ? "- Invitez des coachs" : "- Pas encore de filleuls"}
                      </Typography>
                    </Stack>
                  )}

                  {sponsorshipMetrics!.hasSponsors ? (
                    <Stack direction={{ xs: "column", sm: "column", md: "column", lg: "row" }} width={"100%"} spacing={1.5} flexWrap="wrap">
                      {sponsorshipCards.map((metric, i) => (
                        <Stack key={i} direction="row" alignItems="center" spacing={0.75} sx={{ px: 1.4, py: 0.6, borderRadius: 2, bgcolor: `${metric.color ?? primary}0d`, border: "1px solid", borderColor: `${metric.color ?? primary}25`, display: "flex", justifyContent: "space-between" }}>
                          <Box sx={{ color: metric.color ?? primary, display: "flex", "& svg": { fontSize: 14 } }}>{metric.icon}</Box>
                          <Typography variant="caption" sx={{ fontFamily: ff, color: "text.secondary", fontSize: "0.7rem" }}>{metric.subtitle}</Typography>
                          <Typography variant="caption" fontWeight={700} sx={{ fontFamily: ff, color: metric.color ?? primary, fontSize: "0.78rem" }}>{metric.title}</Typography>
                        </Stack>
                      ))}
                    </Stack>
                  ) : null}

                  <ButtonField
                    label={!sponsorshipMetrics!.hasSponsors
                      ? (isBrandActive(ENV_VERIFIED_TRAINER) ? "Inviter un coach" : "Découvrir")
                      : (isBrandActive(ENV_VERIFIED_TRAINER) ? "Gérer" : "Parrainer")}
                    variant="outlined"
                    className="w-full lg:w-1/4"
                    onClick={onNavigateToSponsorship}
                    endIcon={<ArrowForwardIcon />}
                  />
                </Stack>
              </Box>
            )}
          </Box>

          {/* ── Graphique ────────────────────────────────────────────── */}
          <AllBordChart />

        </div>

        {/* Colonne droite : Sidebar Actions / Santé / Calendrier */}
        <Box sx={{ borderRadius: 3, bgcolor: "background.paper", border: "1px solid", borderColor: "divider", boxShadow: "0 1px 4px rgba(0,0,0,0.04)", display: "flex", flexDirection: "column" }}>
          <Box sx={{ display: { xs: "none", lg: "block" } }}>{quickActions}</Box>

          {/* Nouveautés : dernières notifications non lues (store partagé avec la cloche) */}
          <Divider sx={{ display: { xs: "none", lg: "block" } }} />
          <Box sx={{ px: { xs: 2, md: 2.5 }, py: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ fontFamily: ff, color: primary, mb: 1.5 }}>
              🔔 Nouveautés
            </Typography>
            <NotificationsWidget />
          </Box>

          {/* Aide et contact : mail, téléphone et formulaire vers l'équipe */}
          <Box sx={{ px: { xs: 2, md: 2.5 }, pb: 2 }}>
            <HelpCard compact />
          </Box>

          {totalClients > 0 && (
            <>
              <Divider />
              <Box sx={{ px: { xs: 2, md: 2.5 }, py: 2 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ fontFamily: ff, color: primary, mb: 1.5 }}>
                  👥 Santé clients
                </Typography>
                <Stack spacing={1.2}>
                  <Box>
                    <Stack direction="row" justifyContent="space-between" mb={0.5}>
                      <Typography variant="caption" sx={{ fontFamily: ff, fontWeight: 600, color: "text.primary" }}>Actifs</Typography>
                      <Typography variant="caption" sx={{ fontFamily: ff, color: primary, fontWeight: 700 }}>{totalStudents}</Typography>
                    </Stack>
                    <LinearProgress variant="determinate" value={activePercent} sx={{ height: 6, borderRadius: 4, bgcolor: `${primary}18`, "& .MuiLinearProgress-bar": { borderRadius: 4, background: `linear-gradient(90deg, ${primary}, ${secondary})` } }} />
                  </Box>
                  {totalStudentsNoActive > 0 && (
                    <Box>
                      <Stack direction="row" justifyContent="space-between" mb={0.5}>
                        <Typography variant="caption" sx={{ fontFamily: ff, fontWeight: 600, color: "text.secondary" }}>En attente</Typography>
                        <Typography variant="caption" sx={{ fontFamily: ff, color: "text.secondary", fontWeight: 700 }}>{totalStudentsNoActive}</Typography>
                      </Stack>
                      <LinearProgress variant="determinate" value={100 - activePercent} sx={{ height: 6, borderRadius: 4, bgcolor: "grey.100", "& .MuiLinearProgress-bar": { borderRadius: 4, bgcolor: "warning.light" } }} />
                    </Box>
                  )}
                  <Typography variant="caption" sx={{ color: "text.secondary", fontFamily: ff }}>
                    {totalClients} élève{totalClients > 1 ? "s" : ""} au total • {Math.round(activePercent)}% actifs
                  </Typography>
                </Stack>
              </Box>
            </>
          )}

          <Divider />
          <Box sx={{ px: { xs: 2, md: 2.5 }, pt: 1.5, pb: 0.5, display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ fontSize: "1rem", lineHeight: 1 }}>🗓️</Typography>
            <Typography variant="subtitle2" fontWeight={700} sx={{ fontFamily: ff, color: primary }}>À venir</Typography>
            {course.length > 0 && (
              <Box sx={{ ml: "auto", px: 1.1, py: 0.15, borderRadius: 10, bgcolor: `${primary}14` }}>
                <Typography variant="caption" fontWeight={700} sx={{ fontFamily: ff, color: primary, fontSize: "0.65rem" }}>{course.length}</Typography>
              </Box>
            )}
          </Box>
          <UpcomingCalendar courses={course} initialDays={2} />
        </Box>

      </div>

    </div>
  );
};

export default AllBordTab;
